import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from "react-redux";
import {setShowEmojiModal} from "../../../actions/ui";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFaceSmile, faPaste} from "@fortawesome/free-regular-svg-icons";
import Button from "../Button";
import "./../ReplyForm/ReplyForm.scss"
import EmojiContainer from "../EmojiContainer";
import {errorNotify} from "../../../utils/app";
import Translate from "../CommentItem/Actions/Translate";
import {sendMessage} from "../../../actions/direct_messages";
import SavedRepliesModal from "../SavedRepliesModal";
import { MentionsInput, Mention } from 'react-mentions'

// minimal styles for react-mentions (kept tiny to avoid layout changes)
const mentionsStyle = {
    width: '100%',
    minHeight: 90,
}
const suggestionsListStyle = {
    zIndex: 9999,
    background: '#fff',
    overflow: 'hidden',
}
const suggestionItemBase = { padding: '6px 10px', cursor: 'pointer' }

// react-mentions markup -> plain text (so we can submit a simple string, like before)
const toPlainText = (markupValue) =>
    (markupValue || '').replace(/@\[(.+?)\]\((.+?)\)/g, '@$1')

// insert at caret (used by emojis & saved replies)
function insertViaTextarea(textareaEl, text) {
    if (!textareaEl) return
    textareaEl.focus()
    let ok = false
    try { ok = document.execCommand('insertText', false, text) } catch {}
    if (ok) return

    const { selectionStart, selectionEnd, value } = textareaEl
    const before = value.slice(0, selectionStart)
    const after = value.slice(selectionEnd)
    const next = before + text + after
    const caret = before.length + text.length
    textareaEl.value = next
    textareaEl.setSelectionRange(caret, caret)
    textareaEl.dispatchEvent(new Event('input', { bubbles: true }))
}

const MessagesReply = ({selectedMessage, pageId}) => {
    const dispatch = useDispatch()
    const msgRef = useRef()            // external ref expected by Translate & modals
    const textareaRef = useRef(null)   // the real textarea inside MentionsInput
    const {sender_id, sender_name, message, uuid} = selectedMessage.attributes
    const [showSavedReplies, setShowSavedReplies] = useState(false)

    // state replacing Tagify’s internal value
    const [markupValue, setMarkupValue] = useState('')
    const [plainValue, setPlainValue] = useState('')
    const [mentionsState, setMentionsState] = useState([])

    // focus on mount (replace tagify DOM focus)
    useEffect(()=>{
        textareaRef.current?.focus()
    },[])

    // keep msgRef shape usable by existing code (Translate expects .current.value / .focus())
    useEffect(() => {
        msgRef.current = {
            value: plainValue,
            focus: () => textareaRef.current?.focus(),
        }
    }, [plainValue])

    // Mentions change handler (keeps things minimal)
    const handleChange = (e, newMarkup, newPlain, newMentions) => {
        setMarkupValue(newMarkup)
        setPlainValue(newPlain)              // plain string for submit
        setMentionsState(newMentions || [])
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        const value = plainValue || ''
        if (value.trim()) {
            const formData = new FormData()
            formData.append("direct_message[message]", value)
            dispatch(sendMessage(selectedMessage.id, formData))

            // clear input (replace tagify DOM clear)
            setMarkupValue('')
            setPlainValue('')
            setMentionsState([])
            if (textareaRef.current) {
                textareaRef.current.value = ''
                textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }))
            }
        } else {
            errorNotify("Please fill in the reply field.")
        }
    }

    // saved replies & emoji insertion keep working via caret API
    const setMessage = (text) => insertViaTextarea(textareaRef.current, text)
    const handleEmojiSelect = (emoji) => setMessage(emoji.native)

    return (
        <>
            <div className="reply-collapse">
                <div className="reply-block mb-3">
                    <div className="w-100" >
                        <div className="text-group w-100 mb-2">
                            {/* TagInput → MentionsInput (only change) */}
                            <MentionsInput
                                value={markupValue}
                                onChange={handleChange}
                                placeholder="Type your message here…"
                                inputRef={(el) => { textareaRef.current = el }}
                                style={mentionsStyle}
                            >
                                {/* provide a Mention child to satisfy react-mentions; empty data = no suggestions (closest to your original) */}
                                <Mention
                                    trigger="@"
                                    data={[]}
                                    markup="@[__display__](__id__)"
                                    displayTransform={(id, display) => `@${display}`}
                                    style={suggestionsListStyle}
                                    renderSuggestion={(entry, search, highlighted, index, focused) => (
                                        <div
                                            key={entry.id || index}
                                            style={{ ...suggestionItemBase, background: focused ? '#f3f4f6' : '#fff' }}
                                        >
                                            {highlighted}
                                        </div>
                                    )}
                                />
                            </MentionsInput>
                        </div>

                        <div className="d-flex justify-content-between mx-0 reply-bottom">
                            <div className="d-flex reply-p p-0">

                            </div>

                            <div className="d-flex reply-b p-0 icon-buttons position-relative">

                                <FontAwesomeIcon icon={faPaste}
                                                 className="saved-replies-icon mx-2"
                                                 size="lg"
                                                 onClick={()=>{setShowSavedReplies(true)}}/>

                                <Translate pageId={pageId} objectId={selectedMessage.id} isMobile={true} dm={true}
                                           className="btn btn-tr saved-replies-icon p-0 mx-2"
                                           inputRef={msgRef}/>

                                <FontAwesomeIcon icon={faFaceSmile}
                                                 className="emoji-icon mx-2"
                                                 size="lg"
                                                 onClick={()=>{dispatch(setShowEmojiModal(null, "comment_message"))}}/>

                                {!showSavedReplies && <EmojiContainer className="emoji-wrapper" handleEmojiSelect={handleEmojiSelect}/>}

                            </div>
                        </div>
                    </div>
                </div>
                <div className="d-flex justify-content-between mb-3">
                    <div className="d-flex">
                        {/*<AiAction objectId={selectedMessage.id} pageId={pageId} msgRef={msgRef} setMessage={setMessage} dm={true}/>*/}
                    </div>
                    <div className="d-flex reply-p p-0">
                        <Button type="submit" className="btn btn-primary" clickHandler={handleSubmit}>Reply</Button>
                    </div>
                </div>
            </div>

            {showSavedReplies && <SavedRepliesModal setShowSavedReplies={setShowSavedReplies} pageId={pageId}
                                                    setMessage={setMessage}/>}
        </>
    )
}

export default React.memo(MessagesReply)
